import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider } from "react-hook-form";
import { apiGet, apiPost, apiPut } from "../../utils/apiFetch";
import apiPath from "../../utils/apiPath";
import useToastContext from "../../hooks/useToastContext";
import OButton from "../../components/reusable/OButton";

import BasicStep from "./BasicStep";
import JobDetailsStep from "./JobDetailsStep";
import PositionStep from "./PositionStep";
import CompensationStep from "./CompensationStep";
import RequirementsStep from "./RequirementsStep";
import QuestionsStep from "./QuestionsStep";
import MetaStep from "./MetaStep";
import SummaryStep from "./SummaryStep";
import AuthContext from "context/AuthContext";

const STEPS = [
  { id: 0, title: "Basic Information", component: BasicStep },
  { id: 1, title: "Job Details", component: JobDetailsStep },
  { id: 2, title: "Position", component: PositionStep },
  { id: 3, title: "Compensation", component: CompensationStep },
  { id: 4, title: "Requirements", component: RequirementsStep },
  { id: 5, title: "Questions", component: QuestionsStep },
  { id: 6, title: "Meta Information", component: MetaStep },
  { id: 7, title: "Summary", component: SummaryStep },
];

const DEFAULTS = {
  status: "active",
  currency: "AED",
  isNegotiable: false,
  isUrgent: false,
  isRemote: false,
  questions: [],
};

const CareerForm = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const notification = useToastContext();
  const isEdit = Boolean(params.id);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const draftKey = isEdit ? `careerForm:edit:${params.id}` : "careerForm:add";
  const { user } = useContext(AuthContext);
  const permission = user?.permission?.find((p) => p.manager === "careers");
  const methods = useForm({
    mode: "onChange",
    defaultValues: { ...DEFAULTS },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
    getValues,
    trigger,
  } = methods;

  useEffect(() => {
    const loadData = async () => {
      const draft = localStorage.getItem(draftKey);
      if (!isEdit && draft) {
        try {
          reset({ ...DEFAULTS, ...JSON.parse(draft) });
        } catch (_) {}
      }
      if (isEdit) {
        setLoading(true);
        try {
          const res = await apiGet(`${apiPath.careerById}/${params.id}`);
          const data = res?.data?.results;
          if (data) {
            // Clear any existing draft first
            localStorage.removeItem(draftKey);

            // Prepare form data
            const formData = { ...DEFAULTS };

            Object.keys(data).forEach((k) => {
              if (typeof data[k] !== "object" && data[k] !== undefined) {
                formData[k] = data[k];
              }
            });

            if (data.applicationDeadline) {
              const d = new Date(data.applicationDeadline);
              const y = d.getFullYear();
              const m = String(d.getMonth() + 1).padStart(2, "0");
              const day = String(d.getDate()).padStart(2, "0");
              formData.applicationDeadline = `${y}-${m}-${day}`;
            }

            if (data.salaryRange) {
              formData.salaryMin = Number(data.salaryRange.min) || 0;
              formData.salaryMax = Number(data.salaryRange.max) || 0;
              formData.currency = data.salaryRange.currency || "AED";
              formData.isNegotiable = data.salaryRange.isNegotiable || false;
            } else {
              formData.salaryMin = Number(data.salaryMin) || 0;
              formData.salaryMax = Number(data.salaryMax) || 0;
            }

            ["benefits", "benefitsAr", "skills", "skillsAr"].forEach((key) => {
              if (Array.isArray(data[key])) {
                formData[key] = data[key].join(", ");
              }
            });

            formData.questions = Array.isArray(data.questions)
              ? data.questions.map((q) => ({
                  _id: q._id,
                  question: q.question || "",
                  questionAr: q.questionAr || "",
                  type: q.type === "dropdown" ? "dropdown" : "text",
                  options:
                    Array.isArray(q.options) && q.options.length
                      ? q.options.map((o) =>
                          typeof o === "string"
                            ? { text: o, textAr: "" }
                            : {
                                text: o?.text || "",
                                textAr: o?.textAr || "",
                              },
                        )
                      : [],
                }))
              : [];

            // Reset form with all data at once
            reset(formData);
          }
        } catch (error) {
          notification.error(
            t("Failed to load career data") || "Failed to load career data",
          );
        } finally {
          setLoading(false);
        }
      }
    };
    loadData();
  }, [isEdit, params.id]);

  useEffect(() => {
    const fromHash = new URL(window.location.href).hash;
    const match = fromHash.match(/step=(\d+)/);
    if (match) {
      const idx = Number(match[1]);
      if (!Number.isNaN(idx) && idx >= 0 && idx < STEPS.length)
        setCurrentStep(idx);
    }
  }, []);
  useEffect(() => {
    const url = new URL(window.location.href);
    url.hash = `step=${currentStep}`;
    window.history.replaceState(null, "", url.toString());
  }, [currentStep]);

  const watched = watch();
  useEffect(() => {
    const h = setTimeout(() => {
      try {
        const {
          title,
          titleAr,
          description,
          descriptionAr,
          requirements,
          requirementsAr,
          responsibilities,
          responsibilitiesAr,
          location,
          locationAr,
          department,
          departmentAr,
          employmentType,
          employmentTypeAr,
          experienceLevel,
          experienceLevelAr,
          applicationDeadline,
          salaryMin,
          salaryMax,
          currency,
          isNegotiable,
          isUrgent,
          isRemote,
          benefits,
          benefitsAr,
          skills,
          skillsAr,
          status,
          metaTitle,
          metaTitleAr,
          metaDescription,
          metaDescriptionAr,
          metaKeyword,
          metaKeywordAr,
          questions,
        } = getValues();
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            title,
            titleAr,
            description,
            descriptionAr,
            requirements,
            requirementsAr,
            responsibilities,
            responsibilitiesAr,
            location,
            locationAr,
            department,
            departmentAr,
            employmentType,
            employmentTypeAr,
            experienceLevel,
            experienceLevelAr,
            applicationDeadline,
            salaryMin,
            salaryMax,
            currency,
            isNegotiable,
            isUrgent,
            isRemote,
            benefits,
            benefitsAr,
            skills,
            skillsAr,
            status,
            metaTitle,
            metaTitleAr,
            metaDescription,
            metaDescriptionAr,
            metaKeyword,
            metaKeywordAr,
            questions,
          }),
        );
      } catch (_) {}
    }, 500);
    return () => clearTimeout(h);
  }, [watched]);

  const clearDraft = () => {
    try {
      localStorage.removeItem(draftKey);
    } catch (_) {}
  };

  const isStepValid = (index) => {
    const StepComp = STEPS[index].component;
    const fields = StepComp?.fields || [];
    if (!fields.length) return true;
    const vals = getValues();
    for (const f of fields) {
      const v = vals[f];
      if (
        v === undefined ||
        v === null ||
        v === "" ||
        (typeof v === "string" && v.trim() === "")
      )
        return false;
      if (errors[f]) return false;
    }
    return true;
  };

  const canNavigateTo = (index) => {
    if (index <= currentStep) return true;
    // Ensure all previous steps are valid
    for (let i = 0; i < index; i++) {
      if (!isStepValid(i)) return false;
    }
    return true;
  };

  const validateQuestions = () => {
    const questions = getValues("questions") || [];
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      if (!String(q?.question || "").trim()) {
        return (
          t("Question {{n}} needs text", { n: i + 1 }) ||
          `Question ${i + 1} needs text`
        );
      }
      if (q.type === "dropdown") {
        const opts = (q.options || [])
          .map((o) =>
            typeof o === "string" ? o.trim() : String(o?.text || "").trim(),
          )
          .filter(Boolean);
        if (opts.length < 2) {
          return (
            t("Question {{n}} needs at least 2 dropdown options", {
              n: i + 1,
            }) || `Question ${i + 1} needs at least 2 dropdown options`
          );
        }
      }
    }
    return null;
  };

  const onSubmit = async (formData) => {
    const questionsError = validateQuestions();
    if (questionsError) {
      notification.error(questionsError);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        salaryMin: Number(formData.salaryMin) || 0,
        salaryMax: Number(formData.salaryMax) || 0,
        benefits: formData.benefits
          ? formData.benefits
              .split(",")
              .map((b) => b.trim())
              .filter(Boolean)
          : undefined,
        benefitsAr: formData.benefitsAr
          ? formData.benefitsAr
              .split(",")
              .map((b) => b.trim())
              .filter(Boolean)
          : undefined,
        skills: formData.skills
          ? formData.skills
              .split(",")
              .map((b) => b.trim())
              .filter(Boolean)
          : undefined,
        skillsAr: formData.skillsAr
          ? formData.skillsAr
              .split(",")
              .map((b) => b.trim())
              .filter(Boolean)
          : undefined,
        questions: Array.isArray(formData.questions)
          ? formData.questions
              .filter((q) => q && String(q.question || "").trim())
              .map((q) => ({
                question: String(q.question).trim(),
                questionAr: String(q.questionAr || "").trim(),
                type: q.type === "dropdown" ? "dropdown" : "text",
                options:
                  q.type === "dropdown" && Array.isArray(q.options)
                    ? q.options
                        .map((o) =>
                          typeof o === "string"
                            ? { text: o.trim(), textAr: "" }
                            : {
                                text: String(o?.text || "").trim(),
                                textAr: String(o?.textAr || "").trim(),
                              },
                        )
                        .filter((o) => o.text)
                    : [],
              }))
          : [],
        applicationDeadline: formData.applicationDeadline || undefined,
        status: formData.status || "active",
      };

      const res = isEdit
        ? await apiPut(`${apiPath.careerById}/${params.id}`, payload)
        : await apiPost(apiPath.careers, payload);

      if (res?.data?.success) {
        clearDraft();
        notification.success(res?.data?.message || t("Saved"));
        navigate("/careers");
      } else {
        notification.error(res?.data?.message || t("O_SOMETHING_WENT_WRONG"));
      }
    } catch (e) {
      notification.error(e?.message || t("O_SOMETHING_WENT_WRONG"));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    const StepComp = STEPS[currentStep].component;
    const fields = StepComp?.fields || [];
    if (fields.length > 0) {
      const valid = await trigger(fields);
      if (!valid) {
        notification.error(
          t("Please fix errors before proceeding") ||
            "Please fix errors before proceeding",
        );
        return;
      }
    }
    if (StepComp === QuestionsStep) {
      const questionsError = validateQuestions();
      if (questionsError) {
        notification.error(questionsError);
        return;
      }
    }
    if (currentStep < STEPS.length - 1) {
      setTimeout(() => setCurrentStep((prev) => prev + 1), 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const StepComponent = STEPS[currentStep].component;

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") return nextStep(e);
    if (e.key === "ArrowLeft") return prevStep();
    if (e.key === "Enter" && currentStep < STEPS.length - 1) return nextStep(e);
    return undefined;
  };

  return (
    <div className="p-4 max-w-6xl mx-auto" onKeyDown={onKeyDown} tabIndex={0}>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit, () =>
            notification.error(
              t("Please fix errors before submitting") ||
                "Please fix errors before submitting",
            ),
          )}
          className="space-y-6"
        >
          {/* Stepper */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex-1 flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer ${
                    index < currentStep
                      ? "bg-green-500 text-white"
                      : index === currentStep
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Step ${index + 1}: ${step.title}`}
                  onClick={() => {
                    if (canNavigateTo(index)) setCurrentStep(index);
                    else
                      notification.error(
                        t("Please complete previous step") ||
                          "Please complete previous step",
                      );
                  }}
                >
                  {isStepValid(index) ? "✓" : index + 1}
                </div>
                {index < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      index < currentStep ? "bg-green-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            ))}
            <div className="ml-3 text-sm text-gray-600">
              {currentStep + 1}/{STEPS.length}
            </div>
          </div>

          {/* Current Step */}
          <div className="bg-white rounded-lg border shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6 text-gray-800">
              Step {currentStep + 1}: {STEPS[currentStep].title}
              {isEdit && " (Edit Mode)"}
            </h2>
            <StepComponent
              register={methods.register}
              errors={errors}
              permission={permission}
              user={user}
              setValue={setValue}
              watch={watch}
              isEdit={isEdit}
            />
          </div>

          {/* Navigation - sticky on mobile */}
          <div className="flex justify-between pt-6 border-t sticky bottom-0 bg-gray-50 md:bg-transparent md:static z-10">
            {currentStep > 0 && (
              <OButton
                type="button"
                variant="outline"
                label={t("Previous")}
                onClick={prevStep}
                disabled={loading}
              />
            )}
            <div className="flex gap-3 ml-auto">
              {currentStep < STEPS.length - 1 ? (
                <OButton
                  type="button"
                  label={t("Next")}
                  onClick={nextStep}
                  disabled={loading}
                />
              ) : (
                <OButton
                  type="submit"
                  label={isEdit ? t("O_UPDATE") : t("O_ADD")}
                  disabled={loading}
                  loading={loading}
                />
              )}
              <OButton
                type="button"
                variant="outline"
                label={t("Cancel")}
                onClick={() => navigate("/careers")}
                disabled={loading}
              />
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default CareerForm;
